import { DOCUMENT, EnvironmentProviders, inject, provideAppInitializer, REQUEST, RESPONSE_INIT } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { firstValueFrom } from 'rxjs';
import { splitLangPrefix } from './lang-prefix';

/**
 * Resolves the active language from the request (SSR) or URL (browser) and loads
 * its translations BEFORE the first render and before the router matches any
 * (lazy) route.
 *
 * This removes the SSR race where ShellLocalize.childRoutes()'s `canMatch` read
 * `translateService.currentLang` before AppComponent.ngOnInit had set it, and
 * guarantees the server renders the correct locale, `<html lang>` and the
 * Content-Language response header.
 */
export function provideLanguageInitializer(): EnvironmentProviders {
    return provideAppInitializer(() => {
        const translate = inject(TranslateService);
        const document = inject(DOCUMENT);
        const request = inject(REQUEST, { optional: true });
        const responseInit = inject(RESPONSE_INIT, { optional: true });

        const pathname = request ? new URL(request.url).pathname : (document.location?.pathname ?? '');
        const { lang } = splitLangPrefix(pathname.split('/').filter(Boolean));

        translate.addLangs(environment.supportedLanguages);
        translate.setFallbackLang(environment.defaultLanguage);

        document.documentElement.lang = lang;

        // SSR only: stamp Content-Language on the response the engine builds after
        // rendering. RESPONSE_INIT is null on the browser / during SSG, so this is
        // a no-op there.
        if (responseInit?.headers instanceof Headers) {
            responseInit.headers.set('Content-Language', lang);
        }

        return firstValueFrom(translate.use(lang));
    });
}
